
import type { MultiPolygon, Point, Polygon, Position } from 'geojson';
import * as L from 'leaflet';

export class LeafletUtils {

  public static geojsonMultiPolygonToLeafletPolygons(mpoly: MultiPolygon): L.Polygon[] {
    const polys: L.Polygon[] = [];

    mpoly.coordinates.forEach(poly => {
      const polyCoords: number[][][] = [];

      poly.forEach(ring => {
        const ringCoords: number[][] = [];

        ring.forEach(coords => {
          ringCoords.push([coords[1], coords[0]]);
        });

        ringCoords.pop();

        polyCoords.push(ringCoords);
      });

      polys.push(new L.Polygon(polyCoords as L.LatLngExpression[][]));
    });

    return polys;
  }

  public static geojsonPolygonToLeafletPolygon(poly: Polygon): L.Polygon {
    const polyCoords: number[][][] = [];

    poly.coordinates.forEach(ring => {
      const ringCoords: number[][] = [];

      ring.forEach(coords => {
        ringCoords.push([coords[1], coords[0]]);
      });

      ringCoords.pop();

      polyCoords.push(ringCoords);
    });

    return new L.Polygon(polyCoords as L.LatLngExpression[][]);
  }

  public static leafletLayerGroupPolysToMultiPolygon(layergroup: L.LayerGroup): MultiPolygon | undefined {
    const mpCoords: Position[][][] = [];

    layergroup.getLayers().forEach((layer: L.Layer) => {
      if (layer instanceof L.Polygon) {
        const poly = layer.toGeoJSON().geometry;
        mpCoords.push(poly.coordinates as Position[][]);
      }
    });

    if (mpCoords.length === 0) {
      return undefined;
    }

    return {
      coordinates: mpCoords,
      type: 'MultiPolygon',
    };
  }

  public static geoJsonPointToLatLng(point: Point): L.LatLng {
    return new L.LatLng(point.coordinates[1], point.coordinates[0]);
  }

  public static multipolygonToLeafletBounds(polygon: MultiPolygon): L.LatLngBounds {
    const lpolies = LeafletUtils.geojsonMultiPolygonToLeafletPolygons(polygon);
    const layer = new L.FeatureGroup(lpolies);

    return layer.getBounds();
  }

  /**
   * Approximates an L.Circle as an L.Polygon by sampling points around the circumference.
   * Uses a geographic offset calculation that is accurate for typical stop boundary sizes.
   * @param circle The Leaflet Circle to approximate
   * @param numPoints Number of vertices in the resulting polygon (default 64)
   */
  public static circleToPolygon(circle: L.Circle, numPoints = 16): L.Polygon {
    const center = circle.getLatLng();
    const radiusM = circle.getRadius();
    const latPerMeter = 1 / 111320;
    const lngPerMeter = 1 / (111320 * Math.cos(center.lat * (Math.PI / 180)));
    const coords: L.LatLngExpression[] = [];

    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * 2 * Math.PI;
      coords.push([
        center.lat + (radiusM * latPerMeter * Math.cos(angle)),
        center.lng + (radiusM * lngPerMeter * Math.sin(angle)),
      ]);
    }

    return new L.Polygon(coords);
  }

  /**
   * Returns the convex hull of the given points using the Graham scan algorithm.
   * Points are in Leaflet LatLng format (lat, lng). The result is a counterclockwise
   * ordered list of hull vertices suitable for constructing an L.Polygon.
   */

  public static convexHull(points: L.LatLng[]): L.LatLng[] {
    const cross = (origin: L.LatLng, ptA: L.LatLng, ptB: L.LatLng): number => {
      return ((ptA.lng - origin.lng) * (ptB.lat - origin.lat)) - ((ptA.lat - origin.lat) * (ptB.lng - origin.lng));
    };

    // Deduplicate
    const seen = new Set<string>();
    const unique = points.filter(pt => {
      const key = `${pt.lat},${pt.lng}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });

    if (unique.length < 3) {
      return unique;
    }

    // Find pivot: min lat, then min lng on tie
    let pivotIdx = 0;
    for (let i = 1; i < unique.length; i++) {
      if (unique[i].lat < unique[pivotIdx].lat ||
          (unique[i].lat === unique[pivotIdx].lat && unique[i].lng < unique[pivotIdx].lng)) {
        pivotIdx = i;
      }
    }
    const pivot = unique[pivotIdx];

    // Sort by polar angle from pivot (counterclockwise from east); collinear points by distance
    const others = unique.filter(pt => pt !== pivot).sort((a, b) => {
      const ax = a.lng - pivot.lng,
        ay = a.lat - pivot.lat;
      const bx = b.lng - pivot.lng,
        by = b.lat - pivot.lat;
      const da = Math.atan2(ay, ax),
        db = Math.atan2(by, bx);
      if (Math.abs(da - db) > 1e-10) {
        return da - db;
      }
      return ((ax * ax) + (ay * ay)) - ((bx * bx) + (by * by));
    });

    // Scan: pop right turns and collinear points
    const hull: L.LatLng[] = [pivot];
    for (const pt of others) {
      while (hull.length >= 2 && cross(hull[hull.length - 2], hull[hull.length - 1], pt) <= 0) {
        hull.pop();
      }
      hull.push(pt);
    }

    return hull;
  }

  public static multipolygonsToLeafletBounds(polygons: MultiPolygon[]): L.LatLngBounds | null {
    let allBounds: L.LatLngBounds | null = null;

    polygons.forEach((polygon: MultiPolygon) => {
      const lpolies = LeafletUtils.geojsonMultiPolygonToLeafletPolygons(polygon);

      const layer = new L.FeatureGroup(lpolies);
      const bounds = layer.getBounds();

      allBounds = allBounds === null ? bounds : allBounds.extend(bounds);
    });


    return allBounds;
  }

}
